import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { TextField, Button, Typography, Box, CircularProgress, IconButton, InputAdornment, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle, Cancel } from '@mui/icons-material';
import { ToastContainer, toast } from 'react-toastify';

// API
import { resetPassword } from '../api/auth';

const PasswordResetForm = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const { token } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        if (!token) {
            setMessage('Invalid reset link. Please request a new one.');
        }
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Password validation
        if (!/(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}/.test(password)) {
            toast.error('Password must contain at least one letter, one number, one symbol, and be 8 characters long.');
            return;
        }

        if (password !== confirmPassword) {
            toast.error('Passwords do not match. Please try again.');
            return;
        }

        setIsLoading(true);
        try {
            const response = await resetPassword(password, token);
            setMessage(response.data.message);
            toast.success(response.data.message);
            setTimeout(() => navigate('/'), 3000); // Redirect to login page after 3 seconds
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'An error occurred. Please try again.';
            setMessage(errorMessage);
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleClickShowConfirmPassword = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const passwordCriteria = [
        { label: 'At least 8 characters', test: (pw) => pw.length >= 8 },
        { label: 'At least one letter', test: (pw) => /[A-Za-z]/.test(pw) },
        { label: 'At least one number', test: (pw) => /\d/.test(pw) },
        { label: 'At least one symbol', test: (pw) => /[@$!%*?&]/.test(pw) },
    ];

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ToastContainer />
            <Box sx={{
                p: 3, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 3,
                alignItems: 'center', justifyContent: 'center',
            }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    Reset Your Password
                </Typography>
                {message ? (
                    <Typography variant="body2" color="textSecondary" align="center">
                        {message}
                    </Typography>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <TextField
                            label="New Password"
                            type={showPassword ? 'text' : 'password'}
                            fullWidth
                            margin="normal"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowPassword}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <List sx={{ width: '100%', padding: 0 }}>
                            {passwordCriteria.map((criterion, index) => (
                                <ListItem key={index} sx={{ padding: '4px 8px' }}>
                                    <ListItemIcon sx={{ minWidth: 24 }}>
                                        {criterion.test(password) ? <CheckCircle fontSize="0.75rem" color="success" /> : <Cancel fontSize="0.75rem" color="error" />}
                                    </ListItemIcon>
                                    <ListItemText
                                        primaryTypographyProps={{ fontSize: '0.75rem' }}
                                        primary={criterion.label}
                                    />
                                </ListItem>
                            ))}
                        </List>

                        <TextField
                            label="Confirm New Password"
                            type={showConfirmPassword ? 'text' : 'password'}
                            fullWidth
                            margin="normal"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowConfirmPassword}
                                            edge="end"
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        {password && confirmPassword && (
                            <Typography variant="body2" color={password === confirmPassword ? 'green' : 'red'}>
                                {password === confirmPassword ? 'Passwords match' : 'Passwords do not match'}
                            </Typography>
                        )}
                        <Box sx={{ mt: 2, position: 'relative' }}>
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                color="primary"
                                disabled={isLoading}
                            >
                                {isLoading ? 'Resetting...' : 'Reset Password'}
                            </Button>
                            {isLoading && (
                                <CircularProgress
                                    size={24}
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        marginTop: '-12px',
                                        marginLeft: '-12px',
                                    }}
                                />
                            )}
                        </Box>
                    </form>
                )}
            </Box>
        </Box>
    );
};

export default PasswordResetForm;